import React from "react";
import PageHeader from "../components/PageHeader";
import useScrollReveal from "../hooks/useScrollReveal";
import { biography, timeline, profile, stats, multilateral, portraits } from "../mock";
import { GraduationCap, Languages, Award, CheckCircle2 } from "lucide-react";
import TextToSpeech from "../components/TextToSpeech";

export default function About() {
  useScrollReveal();

  return (
    <div>
      <PageHeader
        eyebrow="Biography"
        title="A Life in Diplomacy"
        subtitle="Thirty-seven years representing India across five continents and the world's foremost multilateral forums."
      />

      <section className="py-20 bg-[#faf8f4]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-14">
          {/* Portrait + quick facts */}
          <aside className="lg:col-span-1 reveal">
            <div className="relative">
              <div className="absolute -inset-3 border border-[#b8935a]/40 rounded-lg translate-x-3 translate-y-3" />
              <img
                src={portraits.kurta}
                alt="T.P. Sreenivasan"
                className="relative w-full aspect-[4/5] object-cover rounded-lg shadow-xl"
              />
            </div>

            <div className="mt-10 bg-white rounded-lg border border-[#eae4d8] p-6 space-y-5">
              <FactRow icon={GraduationCap} title="Education" text="First rank, B.A. & M.A. (English), University of Kerala" />
              <FactRow icon={Languages} title="Languages" text="English, Malayalam, Hindi, Japanese, Russian" />
              <FactRow icon={Award} title="Service" text="Indian Foreign Service, 1967–2004" />
            </div>
          </aside>

          {/* Biography text */}
          <div className="lg:col-span-2">
            <h2 className="font-serif text-3xl font-bold text-[#1a2b4a] gold-underline reveal">
              The Full Story
            </h2>
            <div className="mt-8 reveal">
              <TextToSpeech text={biography.join(" ")} label="Listen to biography" />
            </div>
            <div className="mt-10 space-y-6">
              {biography.map((para, i) => (
                <p
                  key={i}
                  className="text-[#3a4155] leading-[1.9] text-[17px] reveal"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="py-16 bg-[#1a2b4a]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center reveal" style={{ transitionDelay: `${i * 80}ms` }}>
              <div className="font-serif text-5xl font-bold text-[#e6c98d]">{s.value}</div>
              <div className="mt-2 text-sm text-white/70">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-[#b8935a] mb-3">Career</p>
            <h2 className="font-serif text-4xl font-bold text-[#1a2b4a] gold-underline center inline-block">
              Diplomatic Postings
            </h2>
          </div>

          <div className="mt-16 relative">
            <div className="absolute left-[7px] md:left-1/2 top-0 bottom-0 w-px bg-[#eae4d8] md:-translate-x-1/2" />
            {timeline.map((t, i) => (
              <div
                key={i}
                className={`relative mb-10 md:w-1/2 pl-10 md:pl-0 reveal ${
                  i % 2 === 0 ? "md:pr-12 md:text-right" : "md:ml-auto md:pl-12"
                }`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <span
                  className={`absolute top-1.5 w-4 h-4 rounded-full bg-[#b8935a] ring-4 ring-[#faf8f4] left-0 md:left-auto ${
                    i % 2 === 0 ? "md:-right-2" : "md:-left-2"
                  }`}
                />
                <p className="text-sm font-semibold text-[#b8935a]">{t.period}</p>
                <p className="mt-2 text-[#3a4155] leading-relaxed">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Multilateral Experience */}
      <section className="py-24 bg-[#faf8f4]">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-[#b8935a] mb-3">
              At the United Nations
            </p>
            <h2 className="font-serif text-4xl font-bold text-[#1a2b4a] gold-underline center inline-block">
              Multilateral Experience
            </h2>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {multilateral.map((m, i) => (
              <div
                key={i}
                className="flex gap-3 items-start reveal"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <CheckCircle2
                  size={20}
                  className="text-[#b8935a] flex-shrink-0 mt-0.5"
                />
                <p className="text-[#3a4155] leading-relaxed">{m}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function FactRow({ icon: Icon, title, text }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-full bg-[#1a2b4a] flex items-center justify-center text-[#e6c98d] flex-shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-[#8a8f9c]">{title}</p>
        <p className="text-[#1a2b4a] font-medium mt-0.5 leading-snug">{text}</p>
      </div>
    </div>
  );
}
